import { InternalId } from '@edgebox/data-definition-kit';
import { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import React, { useEffect, useRef } from 'react';
import { SuggestedFaqDraftState } from './SuggestedFaqs';
import { didTaskGroupWrite, isTaskGroupFinished, TaskGroupSummaryItem } from './taskGroupProgress.utils';

/**
 * Following the draft one added FAQ asked for, until its task group ends.
 */

/** How the page follows the draft it just asked for, matching this area's other task-group follows. */
const DRAFT_POLL_INTERVAL_MS = 2000;
const DRAFT_POLL_LIMIT = 150;
/** After this many reads in a row fail, the row says the state could not be read. */
const DRAFT_UNREADABLE_AFTER_FAILED_READS = 3;
/**
 * At most this many drafts are followed at once. The rest wait their turn and
 * say the same thing meanwhile, so adding ten FAQs does not put ten reads on
 * the wire every two seconds.
 */
export const DRAFT_FOLLOW_CONCURRENCY = 3;

/** A draft this visit asked for, and where it stands. */
export interface IFaqDraft {
  taskGroupId?: string;
  state: SuggestedFaqDraftState;
}

/**
 * Follows one draft's task group until it ends. A read that fails is retried
 * within the follow's own bounds, because a read failure says nothing about the
 * draft: the create already succeeded. Only the group's own failed state means
 * the draft was not written.
 */
export function FaqDraftFollower(props: { api: ISyncCoreApi; taskGroupId: string; onState: (state: SuggestedFaqDraftState) => void }) {
  const { api, taskGroupId } = props;
  // Held in a ref so a re-render never restarts the follow.
  const onStateRef = useRef(props.onState);
  onStateRef.current = props.onState;

  useEffect(() => {
    const onState = (state: SuggestedFaqDraftState) => onStateRef.current(state);
    let active = true;
    let reads = 0;
    let consecutiveFailedReads = 0;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const again = () => {
      timeout = setTimeout(() => {
        void refresh();
      }, DRAFT_POLL_INTERVAL_MS);
    };

    const refresh = async () => {
      reads += 1;

      try {
        const summary = await api.service.taskGroups.summary(taskGroupId as InternalId);
        if (!active) {
          return;
        }

        consecutiveFailedReads = 0;
        const byStatus = (summary?.byStatus || []) as TaskGroupSummaryItem[];

        if (isTaskGroupFinished(byStatus)) {
          onState(didTaskGroupWrite(byStatus) ? 'written' : 'failed');
          return;
        }

        if (reads >= DRAFT_POLL_LIMIT) {
          onState('slow');
          return;
        }

        again();
      } catch {
        if (!active) {
          return;
        }

        consecutiveFailedReads += 1;
        if (consecutiveFailedReads >= DRAFT_UNREADABLE_AFTER_FAILED_READS || reads >= DRAFT_POLL_LIMIT) {
          onState('unreadable');
          return;
        }

        again();
      }
    };

    void refresh();

    return () => {
      active = false;
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [api, taskGroupId]);

  return null;
}
